import { Card, useDialog } from "@fpsoftware/mvp";
import BigNumber from "bignumber.js";
import type { ComponentProps } from "react";
import { formatMoney } from "shared";
import { Badge } from "@/components/Badge/Badge";
import { EditableValue } from "@/components/EditableValue/EditableValue";
import {
  EditMortgageEscrowDialog,
  type EscrowKeys,
} from "@/mortgages/EditMortgageEscrowDialog";
import { useMortgage } from "@/mortgages/hooks";

export function MortgageEscrowBreakdownCard(
  props: { mortgageID: number } & ComponentProps<typeof Card>,
) {
  const { mortgageID, ...rest } = props;
  const mortgage = useMortgage(mortgageID);
  const { open } = useDialog();

  const handleBreakdownOpen = (fieldKey: EscrowKeys) => {
    open(EditMortgageEscrowDialog, {
      mortgageID: mortgage.ID,
      fieldKey,
    });
  };

  const total = [
    new BigNumber(mortgage.data.escrowBreakdown.propertyInsurance ?? 0),
    new BigNumber(mortgage.data.escrowBreakdown.mortgageInsurance ?? 0),
    new BigNumber(mortgage.data.escrowBreakdown.propertyTax ?? 0),
  ].reduce((acc, curr) => acc.plus(curr), new BigNumber(0));

  const difference = total
    ? new BigNumber(mortgage.data.payments.escrow).minus(total)
    : null;

  return (
    <Card {...rest}>
      <Card.Header>
        Escrow payment breakdown
        <span style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
          {difference && !difference.eq(0) && (
            <Badge variant="error">{formatMoney(difference)} difference</Badge>
          )}
          {formatMoney(mortgage.data.payments.escrow)}/mo
        </span>
      </Card.Header>
      <Card.List>
        <Card.ListItem label="Property taxes">
          {formatMoney(mortgage.data.escrowBreakdown.propertyTax)}/mo
        </Card.ListItem>
        <Card.ListItem label="Property Insurance">
          <EditableValue
            label={`${formatMoney(mortgage.data.escrowBreakdown.propertyInsurance)}/mo`}
            onEdit={() => handleBreakdownOpen("monthlyPropertyInsurance")}
          />
        </Card.ListItem>
        <Card.ListItem label="Mortgage Insurance">
          <EditableValue
            label={`${formatMoney(mortgage.data.escrowBreakdown.mortgageInsurance)}/mo`}
            onEdit={() => handleBreakdownOpen("monthlyMortgageInsurance")}
          />
        </Card.ListItem>
      </Card.List>
    </Card>
  );
}
